import { supabase } from '../lib/supabase'
import { User } from '../types'
import { appBaseUrl, emailService } from './emailService'
import { apiHeadersOrThrow } from '../lib/authHeader'

export interface AdminUser extends User {
  permissions: Record<string, boolean>
  is_super_admin: boolean
  is_active: boolean
}

export const usersService = {
  async listUsers(): Promise<AdminUser[]> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, email, full_name, role, phone, permissions, is_super_admin, is_active, created_at, updated_at')
      .order('created_at', { ascending: true })
    if (error) throw error
    return (data || []) as AdminUser[]
  },

  async updateUser(
    id: string,
    patch: Partial<{
      full_name: string
      /* Winnie, 15 Sep: mobile is required on this form. `user_profiles.phone` has existed
         since migration 001 and the admin panel never wrote to it. */
      phone: string
      is_active: boolean
      is_super_admin: boolean
      permissions: Record<string, boolean>
    }>
  ): Promise<void> {
    const { error } = await supabase
      .from('user_profiles')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
  },

  async deactivateUser(id: string): Promise<void> {
    const { error } = await supabase
      .from('user_profiles')
      .update({ is_active: false, permissions: {}, is_super_admin: false, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
  },

  /**
   * Create a new user (auth login + profile). Requires the server-side Pages
   * Function which holds the Supabase service-role key. Returns a friendly
   * error message if the function/key isn't configured yet.
   */
  async createUser(payload: {
    full_name: string
    email: string
    phone: string
    permissions: Record<string, boolean>
    is_super_admin: boolean
  }): Promise<{ success: boolean; message: string }> {
    try {
      const res = await fetch('/api/admin/create-user', {
        method: 'POST',
        // Provisioning uses the service-role key server-side. The caller must
        // prove they are a signed-in super admin before it will run.
        headers: await apiHeadersOrThrow(),
        body: JSON.stringify(payload),
      })
      const ct = res.headers.get('content-type') || ''
      if (!ct.includes('application/json')) {
        return { success: false, message: 'User provisioning not available (admin function not deployed)' }
      }
      const data = await res.json()
      if (!res.ok || !data.success) {
        return { success: false, message: data.error || `Create failed (${res.status})` }
      }
      // A user provisioned here is given a random UUID as their password, so the
      // only way in is the set-password email. Sending it automatically closes
      // the loop that previously left every admin-created user unable to log in.
      const invite = await usersService.sendPasswordSetupEmail(payload.email, payload.full_name)
      return {
        success: true,
        message: invite.success
          ? `${payload.full_name} created. An invitation has been sent to ${payload.email}.`
          : `${payload.full_name} created, but the invitation email failed: ${invite.message} Use "Send set-password email" on their row to retry.`,
      }
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Create failed' }
    }
  },

  /**
   * Send (or re-send) the email that lets a NEW user choose their first password.
   *
   * Winnie, 18 September: the invite and the recovery are two different emails. This is the
   * invite — "An account has been created for you... Set a password to get started."
   *
   * It used to be `supabase.auth.resetPasswordForEmail`, which sends Supabase's own generic
   * password-RESET email. So every person set up on the platform was told their password had
   * been reset, on an account they had never heard of. That is the confusion this replaces.
   *
   * If the branded send cannot be made *because the function is not there* — an environment
   * without it deployed, or without Resend configured — it falls back to the old Supabase
   * email rather than leaving somebody with no way in at all. The wording is worse; having no
   * account is worse still.
   *
   * 22 September: that fallback used to catch every failure, including a refusal. So when the
   * server answered 403, the app quietly sent Supabase's generic "your password has been
   * reset" email instead — the exact email this work removed — and reported success. The
   * administrator was told the invite had gone; the recipient got the wrong one; and the
   * permission problem behind it stayed invisible. A refusal is an answer, and it is reported.
   */
  async sendPasswordSetupEmail(email: string, name?: string): Promise<{ success: boolean; message: string }> {
    const sent = await emailService.sendInvite(email, name)
    if (sent.success) return { success: true, message: `An invitation has been sent to ${email}.` }
    if (!sent.endpointMissing) return { success: false, message: sent.message }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${appBaseUrl()}/auth/reset-password`,
      })
      if (error) throw error
      return {
        success: true,
        message: `Set-password email sent to ${email} (using the fallback template — ${sent.message}).`,
      }
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Could not send the email.' }
    }
  },

  /**
   * The other one: a password reset for somebody who already has an account.
   *
   * Same fallback reasoning as above, and the same deliberate vagueness in the message — this
   * is reachable from the login screen, so it must not confirm whether an address is known.
   */
  async sendPasswordRecoveryEmail(email: string): Promise<{ success: boolean; message: string }> {
    const sent = await emailService.sendPasswordRecovery(email)
    if (sent.success) return { success: true, message: sent.message }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${appBaseUrl()}/auth/reset-password`,
      })
      if (error) throw error
    } catch {
      /* Swallowed on purpose: the answer below is the same either way. */
    }
    return { success: true, message: 'If that address has an account, an email is on its way.' }
  },
}
